import { useState } from "react";
import {
    Box,
    Flex,
    Text,
    Button,
    VStack,
    HStack,
    SimpleGrid,
    Badge,
    Table,
} from "@chakra-ui/react";

import {
    LifeBuoy,
    Users,
    Wallet,
    User,
    Laptop,
    Paperclip,
    Clock3,
    CheckCircle2,
    Trophy,
    Plus,
} from "lucide-react";
import Sidebar from "../../../components/Sidebar";
import TopBar from "../../../components/Topbar";
import SupportTicketModal from "./SupportTicketModal"
import Footer from "./Footer";

const supportExamples = [
    {
        title: "Recruitment & Referrals",
        icon: Users,
        subject:
            "Driver not linked to referral code MG-DP-5673456",
        message:
            "A driver registered using referral code MG-DP-5673456 but does not appear in my recruited drivers list.",
    },
    {
        title: "Commissions & Payouts",
        icon: Wallet,
        subject:
            "Missing commission payout",
        message:
            "One of my recruited drivers reached a commission milestone but the payout does not appear in my commission history.",
    },
    {
        title: "Partner Account",
        icon: User,
        subject:
            "Unable to update banking details",
        message:
            "My banking details are not saving successfully in the portal.",
    },
    {
        title: "Technical Support",
        icon: Laptop,
        subject:
            "Dashboard statistics not loading",
        message:
            "My recruited drivers and commission statistics are missing from the dashboard.",
    },
];

const milestones = [
    {
        milestone:
            "Registration Completed",
        reward: "P0",
    },
    {
        milestone:
            "KYC Submitted (Driver Profile & Vehicle Details)",
        reward: "P0",
    },
    {
        milestone:
            "Driver Approved",
        reward: "P30",
    },
    {
        milestone:
            "First Delivery Completed",
        reward: "P50",
    },
    {
        milestone:
            "10 Deliveries Completed",
        reward: "P70",
    },
    {
        milestone:
            "30 Deliveries Completed",
        reward: "P100",
    },
];

const responseTimes = [
    {
        label:
            "General Questions",
        time: "Within 5 Minutes",
    },
    {
        label:
            "Recruitment Issues",
        time: "Within 10 Minutes",
    },
    {
        label:
            "Commission Questions",
        time: "Within 10 Minutes",
    },
    {
        label:
            "Technical Support",
        time: "Within 15 Minutes",
    },
    {
        label:
            "Account Updates",
        time: "Within 15 Minutes",
    },
];

const SupportGuidePage =
    () => {
        const [isOpen, setIsOpen] =
    useState(false);

const [category, setCategory] =
    useState(
        "RECRUITMENT_AND_REFERRALS"
    );
        return (
              <Flex
                h="100vh"
                bg="#F8FAFC"
                overflow="hidden"
            >
                <Box
                    w={{
                        base: 0,
                        md: "280px",
                    }}
                    flexShrink={0}
                >
                    <Sidebar />
                </Box>

                <Flex
                    flex="1"
                    direction="column"
                    overflow="hidden"
                >
                    <TopBar />

                    <Box
                        flex="1"
                        overflowY="auto"
                        p={{
                            base: 4,
                            md: 8,
                        }}
                    >
            <VStack
                align="stretch"
                gap={8}
            >
               <Flex
    justify="space-between"
    align={{
        base: "flex-start",
        lg: "center",
    }}
    direction={{
        base: "column",
        lg: "row",
    }}
    gap={6}
>
    <Box>
        <HStack mb={3}>
            <LifeBuoy
                size={24}
                color="#2453FF"
            />

            <Text
                fontSize="30px"
                fontWeight="700"
                color="#071D54"
            >
                Driver Partner Support Guide
            </Text>
        </HStack>

        <Text
            color="gray.500"
            lineHeight="1.8"
        >
            Learn how referrals,
            commissions, milestones
            and support requests work
            within the Mogoma Driver
            Partner Program.
        </Text>
    </Box>

    <Button
        bg="#2453FF"
        color="white"
        borderRadius="12px"
        px={6}
        h="48px"
        _hover={{
            bg: "#1E46D8",
        }}
        onClick={() =>
            setIsOpen(true)
        }
    >
        <Plus size={18} />

        Send Request
    </Button>
</Flex>

                <Box
                    bg="white"
                    p={6}
                    borderRadius="16px"
                    border="1px solid"
                    borderColor="gray.200"
                >
                    <Text
                        fontWeight="700"
                        mb={5}
                    >
                        How Driver
                        Recruitment Works
                    </Text>

                    <VStack
                        align="start"
                        gap={4}
                    >
                        <HStack>
                            <Badge
                                colorPalette="blue"
                            >
                                1
                            </Badge>

                            <Text>
                                Share your
                                referral code
                                with
                                prospective
                                drivers.
                            </Text>
                        </HStack>

                        <HStack>
                            <Badge
                                colorPalette="blue"
                            >
                                2
                            </Badge>

                            <Text>
                                Drivers
                                enter your
                                referral
                                code during
                                registration.
                            </Text>
                        </HStack>

                        <HStack>
                            <Badge
                                colorPalette="blue"
                            >
                                3
                            </Badge>

                            <Text>
                                Approved
                                drivers
                                automatically
                                appear in
                                your My
                                Recruits
                                dashboard.
                            </Text>
                        </HStack>

                        <HStack>
                            <Badge
                                colorPalette="blue"
                            >
                                4
                            </Badge>

                            <Text>
                                As drivers
                                complete
                                milestones,
                                commissions
                                become
                                eligible for
                                payout.
                            </Text>
                        </HStack>

                        <HStack>
                            <Badge
                                colorPalette="blue"
                            >
                                5
                            </Badge>

                            <Text>
                                Qualified
                                payouts are
                                automatically
                                reflected in
                                your
                                Commission
                                History.
                            </Text>
                        </HStack>
                    </VStack>
                </Box>

                <Box
                    bg="white"
                    p={6}
                    borderRadius="16px"
                    border="1px solid"
                    borderColor="gray.200"
                >
                    <HStack mb={5}>
                        <Trophy
                            size={20}
                            color="#2453FF"
                        />

                        <Text
                            fontWeight="700"
                        >
                            Recruitment
                            Commission
                            Milestones
                        </Text>
                    </HStack>

                    <Text
                        color="gray.500"
                        mb={5}
                    >
                        Commissions are
                        earned when your
                        recruited drivers
                        become active and
                        achieve delivery
                        milestones.
                    </Text>

                    <Table.Root>
                        <Table.Header>
                            <Table.Row>
                                <Table.ColumnHeader>
                                    Driver
                                    Milestone
                                </Table.ColumnHeader>

                                <Table.ColumnHeader textAlign="end">
                                    Commission
                                </Table.ColumnHeader>
                            </Table.Row>
                        </Table.Header>

                        <Table.Body>
                            {milestones.map(
                                (
                                    item
                                ) => (
                                    <Table.Row
                                        key={
                                            item.milestone
                                        }
                                    >
                                        <Table.Cell>
                                            {
                                                item.milestone
                                            }
                                        </Table.Cell>

                                        <Table.Cell textAlign="end">
                                            <Text
                                                fontWeight="700"
                                                color="#0F766E"
                                            >
                                                {
                                                    item.reward
                                                }
                                            </Text>
                                        </Table.Cell>
                                    </Table.Row>
                                )
                            )}
                        </Table.Body>
                    </Table.Root>

                    <Box
                        mt={5}
                        p={4}
                        bg="#F5F8FF"
                        borderRadius="12px"
                    >
                        <Text
                            fontWeight="700"
                            color="#2453FF"
                        >
                            Maximum
                            Possible Reward
                            Per Quality
                            Driver: P250
                        </Text>
                    </Box>
                </Box>

                <Box
                    bg="white"
                    p={6}
                    borderRadius="16px"
                    border="1px solid"
                    borderColor="gray.200"
                >
                    <Text
                        fontWeight="700"
                        mb={4}
                    >
                        Before Creating a
                        Support Request
                    </Text>

                    <VStack
                        align="start"
                        gap={3}
                    >
                        {[
                            "Referral code (e.g. MG-DP-5673456)",
                            "Driver reference number",
                            "Payout reference number",
                            "Date and time of the issue",
                            "Screenshots or supporting documents",
                            "Any error messages displayed",
                        ].map(
                            (
                                item
                            ) => (
                                <HStack
                                    key={
                                        item
                                    }
                                >
                                    <CheckCircle2
                                        size={
                                            16
                                        }
                                        color="#16A34A"
                                    />

                                    <Text>
                                        {
                                            item
                                        }
                                    </Text>
                                </HStack>
                            )
                        )}
                    </VStack>
                </Box>

                <Box>
                    <Text
                        fontWeight="700"
                        mb={4}
                    >
                        Example Support
                        Requests
                    </Text>

                    <SimpleGrid
                        columns={{
                            base: 1,
                            lg: 2,
                        }}
                        gap={4}
                    >
                        {supportExamples.map(
                            (
                                example
                            ) => (
                                <Box
                                    key={
                                        example.title
                                    }
                                    bg="white"
                                    p={5}
                                    borderRadius="16px"
                                    border="1px solid"
                                    borderColor="gray.200"
                                >
                                    <HStack mb={4}>
                                        <example.icon
                                            size={
                                                18
                                            }
                                            color="#2453FF"
                                        />

                                        <Text fontWeight="700">
                                            {
                                                example.title
                                            }
                                        </Text>
                                    </HStack>

                                    <Text
                                        fontSize="sm"
                                        color="gray.500"
                                    >
                                        Subject
                                    </Text>

                                    <Text
                                        fontWeight="600"
                                        mb={4}
                                    >
                                        {
                                            example.subject
                                        }
                                    </Text>

                                    <Text
                                        fontSize="sm"
                                        color="gray.500"
                                    >
                                        Example
                                        Message
                                    </Text>

                                    <Text
                                        lineHeight="1.8"
                                    >
                                        {
                                            example.message
                                        }
                                    </Text>
                                </Box>
                            )
                        )}
                    </SimpleGrid>
                </Box>

                <Box
                    bg="white"
                    p={6}
                    borderRadius="16px"
                    border="1px solid"
                    borderColor="gray.200"
                >
                    <HStack mb={4}>
                        <Paperclip
                            size={18}
                            color="#2453FF"
                        />

                        <Text
                            fontWeight="700"
                        >
                            Attachments
                        </Text>
                    </HStack>

                    <Text color="gray.500">
                        You may attach
                        screenshots,
                        documents or
                        supporting files
                        when submitting a
                        support request.
                    </Text>

                    <HStack
                        mt={4}
                        gap={3}
                    >
                        <Badge>
                            PNG
                        </Badge>

                        <Badge>
                            JPG
                        </Badge>

                        <Badge>
                            PDF
                        </Badge>

                       
                    </HStack>

                    <Text
                        mt={4}
                        color="gray.500"
                    >
                        Maximum 6 files
                        per request and a
                        maximum size of
                        10MB per file.
                    </Text>
                </Box>

                <Box
                    bg="white"
                    p={6}
                    borderRadius="16px"
                    border="1px solid"
                    borderColor="gray.200"
                >
                    <HStack mb={5}>
                        <Clock3
                            size={18}
                            color="#2453FF"
                        />

                        <Text
                            fontWeight="700"
                        >
                            Expected
                            Response Times
                        </Text>
                    </HStack>

                    <SimpleGrid
                        columns={{
                            base: 1,
                            md: 2,
                        }}
                        gap={4}
                    >
                        {responseTimes.map(
                            (
                                item
                            ) => (
                                <Box
                                    key={
                                        item.label
                                    }
                                    p={4}
                                    bg="#F8FAFC"
                                    borderRadius="12px"
                                >
                                    <Text fontWeight="600">
                                        {
                                            item.label
                                        }
                                    </Text>

                                    <Text
                                        color="#2453FF"
                                        mt={2}
                                        fontWeight="700"
                                    >
                                        {
                                            item.time
                                        }
                                    </Text>
                                </Box>
                            )
                        )}
                    </SimpleGrid>

                    <Text
                        mt={5}
                        color="gray.500"
                    >
                        Response times
                        apply during
                        normal support
                        operating hours.
                    </Text>
                </Box>
                <Footer/>
            </VStack>
             </Box>
                </Flex>
                <SupportTicketModal
    isOpen={isOpen}
    onClose={() =>
        setIsOpen(false)
    }
    category={category}
    setCategory={
        setCategory
    }
/>
            </Flex>
        );
    };

export default SupportGuidePage;